import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search } from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import TransactionPropertyFilterModule from './TransactionPropertyFilterModule';
import TransactionPropertyFilterChip from './TransactionPropertyFilterChip';

/**
 * Transactions filter panel – same open/close and visual design as Events filter.
 * TRANSACTION section: smart contracts; each has per-contract filters (transaction + user props).
 * FILTERS section: global filters with Add.
 */
const TransactionsFilterModule = ({
  filters = [],
  onFiltersChange = () => {},
  teamId,
  selectedContracts = [],
  onSelectedContractsChange = () => {},
  contractPropertyFilters = [],
  onContractPropertyFiltersChange = () => {}
}) => {
  const [isContractPopupOpen, setIsContractPopupOpen] = useState(false);
  const [isTransactionPropertyFilterOpen, setIsTransactionPropertyFilterOpen] = useState(false);
  const [selectedContractForFilter, setSelectedContractForFilter] = useState(null);
  const [editingContractFilter, setEditingContractFilter] = useState(null);
  const [availableContracts, setAvailableContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [contractSearchTerm, setContractSearchTerm] = useState('');
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const addButtonRef = useRef(null);
  const popupRef = useRef(null);
  const filterButtonRefs = useRef({});
  const propertyFilterButtonRefs = useRef({});
  const addGlobalFilterRef = useRef(null);
  const globalFilterButtonRefs = useRef({});

  const [isGlobalFilterPopupOpen, setIsGlobalFilterPopupOpen] = useState(false);
  const [editingGlobalFilter, setEditingGlobalFilter] = useState(null);

  const getTeamName = () => {
    if (!teamId) return null;
    if (typeof teamId === 'number' || (typeof teamId === 'string' && !isNaN(teamId))) {
      return localStorage.getItem('selectedTeam') || String(teamId);
    }
    if (typeof teamId === 'object' && teamId !== null) {
      return teamId.name || teamId.id;
    }
    return String(teamId);
  };

  useEffect(() => {
    if (isContractPopupOpen && addButtonRef.current) {
      const rect = addButtonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPopupPosition({
        top: rect.bottom + scrollY + 8,
        left: rect.left + scrollX
      });
    }
  }, [isContractPopupOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isContractPopupOpen &&
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        addButtonRef.current &&
        !addButtonRef.current.contains(e.target)
      ) {
        setIsContractPopupOpen(false);
      }
    };
    if (isContractPopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isContractPopupOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isContractPopupOpen) setIsContractPopupOpen(false);
    };
    if (isContractPopupOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isContractPopupOpen]);

  useEffect(() => {
    if (!isContractPopupOpen || !teamId) return;
    const fetchContracts = async () => {
      const teamName = getTeamName();
      if (!teamName) return;
      setLoadingContracts(true);
      try {
        const response = await axiosInstance.get(`/smart-contracts/team/${encodeURIComponent(teamName)}`);
        if (response.data?.contracts) {
          const mapped = response.data.contracts.map((c) => ({
            id: c.contractId ?? c.id,
            name: c.name ?? c.contract_name ?? 'Contract',
            blockchain: c.blockchain ?? c.chain,
            address: c.address ?? c.contract_address
          }));
          setAvailableContracts(mapped);
        } else {
          setAvailableContracts([]);
        }
      } catch (err) {
        console.error('Error fetching smart contracts:', err);
        setAvailableContracts([]);
      } finally {
        setLoadingContracts(false);
      }
    };
    fetchContracts();
  }, [isContractPopupOpen, teamId]);

  const handleAddContract = () => {
    setIsContractPopupOpen(true);
    setContractSearchTerm('');
  };

  const handleContractSelect = (contract) => {
    if (selectedContracts.some((c) => c.id === contract.id)) return;
    onSelectedContractsChange([...selectedContracts, contract]);
    setIsContractPopupOpen(false);
  };

  const handleDeleteContract = (contractId) => {
    onSelectedContractsChange(selectedContracts.filter((c) => c.id !== contractId));
    onContractPropertyFiltersChange(contractPropertyFilters.filter((f) => String(f.contractId) !== String(contractId)));
  };

  const handleContractFilters = (contract) => {
    setSelectedContractForFilter(contract);
    setEditingContractFilter(null);
    setIsTransactionPropertyFilterOpen(true);
  };

  const handleContractPropertyFilterAdd = (newFilter) => {
    const sameContract = contractPropertyFilters.filter((f) => String(f.contractId) === String(newFilter.contractId));
    const withConnector = { ...newFilter, connector: sameContract.length === 0 ? 'WHERE' : 'AND' };
    onContractPropertyFiltersChange([...contractPropertyFilters, withConnector]);
    setIsTransactionPropertyFilterOpen(false);
    setSelectedContractForFilter(null);
  };

  const handleContractPropertyFilterUpdate = (id, updates) => {
    onContractPropertyFiltersChange(
      contractPropertyFilters.map((f) => (f.id === id ? { ...f, ...updates } : f))
    );
    setEditingContractFilter(null);
    setIsTransactionPropertyFilterOpen(false);
  };

  const handleContractPropertyFilterRemove = (id) => {
    onContractPropertyFiltersChange(contractPropertyFilters.filter((f) => f.id !== id));
  };

  const handleAddGlobalFilter = () => {
    setEditingGlobalFilter(null);
    setIsGlobalFilterPopupOpen(true);
  };

  const handleGlobalFilterAdd = (filter) => {
    const existing = filters.find((f) => f.id === filter.id);
    const connector = filters.length === 0 ? 'WHERE' : (existing ? (existing.connector || 'AND') : 'AND');
    const withConnector = { ...filter, connector };
    if (existing) {
      onFiltersChange(filters.map((f) => (f.id === filter.id ? withConnector : f)));
    } else {
      onFiltersChange([...filters, withConnector]);
    }
    setIsGlobalFilterPopupOpen(false);
    setEditingGlobalFilter(null);
  };

  const handleGlobalFilterUpdate = (filterId, updates) => {
    onFiltersChange(filters.map((f) => (f.id === filterId ? { ...f, ...updates } : f)));
  };

  const handleGlobalFilterRemove = (filterId) => {
    const next = filters.filter((f) => f.id !== filterId).map((f, i) => ({ ...f, connector: i === 0 ? 'WHERE' : 'AND' }));
    onFiltersChange(next);
    if (editingGlobalFilter?.id === filterId) {
      setEditingGlobalFilter(null);
      setIsGlobalFilterPopupOpen(false);
    }
  };

  const filteredContracts = availableContracts.filter((c) =>
    `${c.name} ${c.blockchain || ''}`.toLowerCase().includes(contractSearchTerm.toLowerCase())
  );

  const contractPopup = isContractPopupOpen && createPortal(
    <div
      ref={popupRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col overflow-hidden"
      style={{
        top: `${popupPosition.top}px`,
        left: `${popupPosition.left}px`,
        width: '320px',
        maxHeight: '320px',
        fontFamily: 'Raleway, sans-serif'
      }}
    >
      <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#475569]" />
          <input
            type="text"
            placeholder="Search contracts..."
            value={contractSearchTerm}
            onChange={(e) => setContractSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d0c46] focus:border-transparent"
          />
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-2">
        {loadingContracts ? (
          <div className="p-4 text-sm text-[#475569] text-center">Loading contracts...</div>
        ) : filteredContracts.length === 0 ? (
          <div className="p-4 text-sm text-[#475569] text-center">
            {contractSearchTerm ? 'No contracts match your search.' : 'No smart contracts found.'}
          </div>
        ) : (
          <div className="space-y-0.5">
            {filteredContracts.map((contract) => {
              const isSelected = selectedContracts.some((c) => c.id === contract.id);
              const label = contract.blockchain
                ? `${contract.name} (${contract.blockchain})`
                : contract.name;
              return (
                <button
                  key={contract.id}
                  type="button"
                  onClick={() => handleContractSelect(contract)}
                  disabled={isSelected}
                  className={`w-full text-left px-3 py-2 text-sm rounded transition-colors flex items-center gap-2 ${
                    isSelected
                      ? 'bg-gray-100 text-[#475569] cursor-default'
                      : 'text-[#0f172a] hover:bg-gray-50'
                  }`}
                >
                  <span className="flex-1 truncate">{label}</span>
                  {isSelected && <span className="text-xs text-[#475569]">Added</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <div className="bg-transparent border border-dotted border-gray-300 rounded-lg overflow-hidden" style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* Transaction Section */}
      <div className="bg-transparent overflow-hidden">
        <div className="pl-4 pr-6 pt-2 pb-3 flex items-center justify-between border-b border-dotted border-gray-300">
          <h3 className="text-xs font-bold uppercase tracking-wide text-[#0f172a]">
            TRANSACTION
          </h3>
        </div>
        <div className="p-6">
          {selectedContracts.length > 0 && (
            <div className="space-y-4 mb-4">
              {selectedContracts.map((contract, index) => {
                if (!filterButtonRefs.current[contract.id]) filterButtonRefs.current[contract.id] = React.createRef();
                const contractFilters = contractPropertyFilters.filter((f) => String(f.contractId) === String(contract.id));
                return (
                  <div key={`${contract.id}-${index}`} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg">
                        <span className="text-sm text-[#0f172a] font-medium">
                          {contract.blockchain ? `${contract.name} (${contract.blockchain})` : contract.name}
                        </span>
                      </div>
                      <button
                        ref={filterButtonRefs.current[contract.id]}
                        type="button"
                        onClick={() => handleContractFilters(contract)}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="Filters"
                      >
                        <Icon name="filter" size={18} color="#475569" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteContract(contract.id)}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="Remove"
                      >
                        <Icon name="delete" size={16} color="#475569" />
                      </button>
                    </div>
                    {contractFilters.length > 0 && (
                      <div className="ml-6 space-y-2">
                        {contractFilters.map((filter, filterIndex) => (
                          <TransactionPropertyFilterChip
                            key={filter.id}
                            filter={filter}
                            isFirst={filterIndex === 0}
                            teamId={teamId}
                            onEdit={() => {
                              setEditingContractFilter(filter);
                              setSelectedContractForFilter(selectedContracts.find((c) => String(c.id) === String(filter.contractId)) || null);
                              setIsTransactionPropertyFilterOpen(true);
                            }}
                            onUpdate={(updates) => handleContractPropertyFilterUpdate(filter.id, updates)}
                            onRemove={() => handleContractPropertyFilterRemove(filter.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div>
            <button
              ref={addButtonRef}
              type="button"
              onClick={handleAddContract}
              className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0f172a] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add</span>
            </button>
          </div>
        </div>
      </div>
      {contractPopup}

      {(selectedContractForFilter || editingContractFilter) && (
        <TransactionPropertyFilterModule
          isOpen={isTransactionPropertyFilterOpen}
          onClose={() => {
            setIsTransactionPropertyFilterOpen(false);
            setSelectedContractForFilter(null);
            setEditingContractFilter(null);
          }}
          triggerRef={
            editingContractFilter
              ? propertyFilterButtonRefs.current[editingContractFilter.id]
              : selectedContractForFilter && filterButtonRefs.current[selectedContractForFilter.id]
          }
          contractId={editingContractFilter?.contractId ?? selectedContractForFilter?.id}
          contractName={editingContractFilter?.contractName ?? selectedContractForFilter?.name}
          teamId={teamId}
          filters={contractPropertyFilters}
          onFiltersChange={(filterOrUpdates) => {
            if (editingContractFilter && filterOrUpdates.id === editingContractFilter.id) {
              handleContractPropertyFilterUpdate(editingContractFilter.id, filterOrUpdates);
            } else {
              handleContractPropertyFilterAdd(filterOrUpdates);
            }
          }}
          existingFilter={editingContractFilter}
        />
      )}

      <div className="border-t border-dotted border-gray-300" />

      {/* Global Filters Section (same UX as Events: WHERE/AND chips, apply over all transactions) */}
      <div className="bg-transparent overflow-hidden">
        <div className="pl-4 pr-6 pt-2 pb-3 flex items-center justify-between border-b border-dotted border-gray-300">
          <h3 className="text-xs font-bold uppercase tracking-wide text-[#0f172a]">
            FILTERS
          </h3>
        </div>
        <div className="p-6">
          {filters.length > 0 && (
            <div className="space-y-2 mb-4">
              {filters.map((filter, filterIndex) => {
                if (!globalFilterButtonRefs.current[filter.id]) {
                  globalFilterButtonRefs.current[filter.id] = React.createRef();
                }
                return (
                  <TransactionPropertyFilterChip
                    key={filter.id}
                    filter={filter}
                    isFirst={filterIndex === 0}
                    teamId={teamId}
                    onEdit={() => {
                      setEditingGlobalFilter(filter);
                      setIsGlobalFilterPopupOpen(true);
                    }}
                    onUpdate={(updates) => handleGlobalFilterUpdate(filter.id, updates)}
                    onRemove={() => handleGlobalFilterRemove(filter.id)}
                  />
                );
              })}
            </div>
          )}
          <div>
            <button
              ref={addGlobalFilterRef}
              type="button"
              onClick={handleAddGlobalFilter}
              className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0f172a] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add</span>
            </button>
          </div>
        </div>
      </div>

      {/* Global filter property picker (Transaction + User properties, applies over all transactions) */}
      {(isGlobalFilterPopupOpen || editingGlobalFilter) && (
        <TransactionPropertyFilterModule
          isOpen={isGlobalFilterPopupOpen || !!editingGlobalFilter}
          onClose={() => {
            setIsGlobalFilterPopupOpen(false);
            setEditingGlobalFilter(null);
          }}
          triggerRef={
            editingGlobalFilter && globalFilterButtonRefs.current[editingGlobalFilter.id]
              ? globalFilterButtonRefs.current[editingGlobalFilter.id]
              : addGlobalFilterRef
          }
          contractId={null}
          contractName={null}
          teamId={teamId}
          filters={filters}
          onFiltersChange={() => {}}
          existingFilter={editingGlobalFilter}
          isGlobalFilter={true}
          onGlobalFilterAdd={handleGlobalFilterAdd}
        />
      )}
    </div>
  );
};

export default TransactionsFilterModule;
